/**
 * 캘린더 보드 뷰 (spec 2026-09-18 §7.2): kanban.js 로 카드를 끌어 그룹 필드를 patch
 * (실행 취소 가능), 열 바닥 빠른 추가(그룹 값 프리필). 잠긴 보드(파생 그룹)는 끌지 못한다.
 */
import { bindKanban } from './kanban.js';
import { api, patchCommand, createCommand, commitQuiet, recordQuiet, showToast, labels } from './calendar-history.js';
import { showContextMenu } from './context-menu.js';
import { splitScope, sendScoped } from './calendar-scope.js';

// 그룹 필드의 patch 값. 다중값 그룹(다중 선택·사람·관계, `data-multi`)에서는 카드가
// 여러 열에 있으므로 옮긴 값 하나만 갈아 끼운다: card 의 `data-gv`(JSON 배열)에서
// 떠난 열의 값을 빼고 도착한 열의 값을 넣는다 (속성 spec §7).
function fieldsFor(board, value, card, from) {
    const f = board.dataset.groupField || '';
    const p = {};
    if (f === 'done') p.done = value === '1' ? '1' : '0';
    else if (f === 'color') p.color = value || 'c1';
    else if (f.startsWith('prop:')) {
        if (board.dataset.multi === '1' && card) {
            let cur = []; try { cur = JSON.parse(card.dataset.gv || '[]') || []; } catch (_) { cur = []; }
            const next = cur.filter((v) => v !== from);
            if (value && !next.includes(value)) next.push(value);
            p[f] = next.length ? JSON.stringify(next) : '[]';
        } else p[f] = value || '';
    }
    return p;
}
function syncCounts(board) {
    board.querySelectorAll('.kb-col').forEach((col) => { const n = col.querySelectorAll('.kb-card').length; col.dataset.count = String(n); const c = col.querySelector('.kb-col-count'); if (c) c.textContent = String(n); });
}
// 카드 옮기기의 반복 범위 (BA1): 완료 그룹은 언제나 그 카드만, 그 밖(색·속성)의 반복 인스턴스는 묻는다.
// 「이후 모두」·「전체」면 정렬 순서는 빼고 그룹 값만 series_scope 로 보낸다 (실행 취소 밖).
function moveWithScope(board, card, before, after, revert) {
    const f = board.dataset.groupField || '';
    const ask = f !== 'done' && !!card.dataset.series;
    (ask ? splitScope([card], 'edit') : Promise.resolve({ scope: 'this', plain: [card], scoped: [] })).then((sp) => {
        if (!sp) { revert(); return; }
        if (sp.scoped.length) {
            const fields = Object.assign({}, after);
            delete fields.sort_order;
            sendScoped(sp.scoped, sp.scope, () => fields);
            return;
        }
        commitQuiet(patchCommand(card.dataset.id, before, after), { refresh: 'now' }).catch(revert);
    });
}
function localISODate(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }

export function setupCalendarBoard() {
    const board = document.getElementById('calBoard');
    if (!board || board.dataset.spaBound) return;
    board.dataset.spaBound = '1';
    bindKanban(board, {
        canDrop: () => !board.dataset.locked,
        onDrop: (card, move) => {
            if (move.before.stage === move.stage && board.dataset.manual !== '1') return; // 같은 열 안에서 놓기만 한 것
            const multi = board.dataset.multi === '1';
            const before = multi ? fieldsFor(board, move.before.stage, card, move.stage) : fieldsFor(board, move.before.stage);
            const after = multi ? fieldsFor(board, move.stage, card, move.before.stage) : fieldsFor(board, move.stage);
            if (board.dataset.manual === '1') { before.sort_order = move.before.order || '0'; after.sort_order = String(move.order); }
            syncCounts(board);
            moveWithScope(board, card, before, after, () => { move.revert(); syncCounts(board); });
        },
    });
    // ── 카드 메뉴 「이동」 (AQ6): 폰에서는 끌기 대신 이것으로 옮긴다 — 데스크톱은 ⋯·우클릭으로 같은 메뉴.
    // 옮기기는 드롭과 같은 patch(다중값 그룹은 떠난 값만 바꾼다, 수동 정렬이면 도착 열의 맨 아래).
    function columnLabel(col) {
        const t = col.querySelector('.kb-col-head .kb-col-title, .kb-col-head .prop-pill');
        return t ? t.textContent.trim() : col.dataset.stage;
    }
    function moveCard(card, toStage) {
        const from = card.dataset.stage || '';
        if (from === toStage || board.dataset.locked) return;
        const col = board.querySelector('.kb-col[data-stage="' + CSS.escape(toStage) + '"]');
        if (!col) return;
        const multi = board.dataset.multi === '1';
        const before = multi ? fieldsFor(board, from, card, toStage) : fieldsFor(board, from);
        const after = multi ? fieldsFor(board, toStage, card, from) : fieldsFor(board, toStage);
        const cards = col.querySelector('.kb-cards');
        const last = Array.from(cards.querySelectorAll('.kb-card')).pop();
        if (board.dataset.manual === '1') {
            before.sort_order = card.dataset.order || '0';
            after.sort_order = String((last ? Number(last.dataset.order) || 0 : 0) + 1);
        }
        const home = card.parentElement, homeNext = card.nextElementSibling;
        cards.appendChild(card); card.dataset.stage = toStage; syncCounts(board);
        moveWithScope(board, card, before, after, () => {
            if (homeNext && homeNext.parentElement === home) home.insertBefore(card, homeNext); else home.appendChild(card);
            card.dataset.stage = from; syncCounts(board);
        });
    }
    function cardMenu(card, x, y) {
        const D = board.dataset;
        const here = card.dataset.stage || '';
        const cols = Array.from(board.querySelectorAll('.kb-col')).filter((c) => (c.dataset.stage || '') !== here);
        const items = [{ label: D.lOpen || '열기', onClick: () => { if (window.openCalendarEvent) window.openCalendarEvent(card); } }];
        if (cols.length) {
            items.push({ separator: true });
            items.push(D.locked
                ? { label: (D.lMove || '이동') + ' — ' + (D.lLocked || ''), disabled: true }
                : { label: D.lMove || '이동', children: cols.map((c) => ({ label: columnLabel(c), onClick: () => moveCard(card, c.dataset.stage || '') })) });
        }
        showContextMenu(x, y, items);
    }
    board.addEventListener('contextmenu', (e) => {
        const card = e.target.closest('.kb-card');
        if (!card || e.target.closest('input')) return;
        e.preventDefault(); e.stopPropagation();
        cardMenu(card, e.clientX, e.clientY);
    });
    board.addEventListener('click', (e) => {
        const more = e.target.closest('.cal-kb-more');
        if (more) {
            e.stopPropagation();
            const r = more.getBoundingClientRect();
            cardMenu(more.closest('.kb-card'), r.left, r.bottom + 4);
            return;
        }
        const add = e.target.closest('.kb-add');
        if (add) { quickAdd(add.closest('.kb-col')); return; }
        const done = e.target.closest('.cal-kb-done');
        if (done) {
            e.stopPropagation();
            const card = done.closest('.kb-card');
            const was = card.dataset.done === '1';
            card.dataset.done = was ? '0' : '1'; card.classList.toggle('is-done', !was);
            commitQuiet(patchCommand(card.dataset.id, { done: was ? '1' : '0' }, { done: was ? '0' : '1' }), { delay: 600 });
            return;
        }
        if (e.target.closest('a, button, input')) return;
        const card = e.target.closest('.kb-card');
        if (card && window.openCalendarEvent) window.openCalendarEvent(card);
    });
    board.addEventListener('keydown', (e) => {
        const card = e.target.closest('.kb-card'); if (!card) return;
        if (e.target !== card) return; // 카드 안의 링·⋯ 단추는 자기 키를 쓴다
        if (e.key === 'Enter') { e.preventDefault(); if (window.openCalendarEvent) window.openCalendarEvent(card); }
        else if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) { e.preventDefault(); const r = card.getBoundingClientRect(); cardMenu(card, r.left + 8, r.top + 24); }
    });
    function quickAdd(col) {
        if (!col || col.querySelector('.cal-kb-quick')) return;
        const box = document.createElement('div'); box.className = 'cal-kb-quick';
        const input = document.createElement('input'); input.type = 'text'; input.maxLength = 200; input.placeholder = board.dataset.lAdd || '';
        box.appendChild(input);
        col.querySelector('.kb-cards').appendChild(box);
        input.focus();
        const close = () => box.remove();
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') { e.preventDefault(); close(); return; }
            if (e.key !== 'Enter') return;
            e.preventDefault();
            const title = input.value.trim(); if (!title) { close(); return; }
            input.disabled = true;
            let prefill = {}; try { prefill = JSON.parse(col.dataset.prefill || '{}'); } catch (_) { prefill = {}; }
            const body = new URLSearchParams();
            body.set('title', title); body.set('all_day', 'on'); body.set('color', prefill.color || 'c1');
            const calId = board.dataset.calId || '';
            body.set('calendar_id', calId);
            const date = calId ? '' : (board.dataset.addDate || localISODate(new Date())); // 프로젝트 작업은 미정, 개인 일정은 날짜 필수
            body.set('start_date', date); body.set('end_date', date);
            const rest = Object.assign({}, prefill); delete rest.color;
            api.create(body)
                .then((ev) => (Object.keys(rest).length ? api.patch(ev.id, rest).then(() => ev) : ev))
                .then((ev) => { close(); return recordQuiet(createCommand(ev.id), { refresh: 'now' }); })
                .catch(() => { input.disabled = false; input.focus(); showToast(labels().failed, { error: true }); });
        });
        input.addEventListener('blur', () => setTimeout(() => { if (!input.value.trim()) close(); }, 150));
    }
}
